import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddCourseDialogComponent } from '../add-course-dialog/add-course-dialog.component';
import { combineLatest, Observable } from 'rxjs';
import { Course } from '../models/course.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../store';
import {
  isAdmin,
  isCourseAdmin,
  isLoggedOut,
  isSubscriberInfoMessageShown,
  isUserSubscribed,
  selectAllCourses,
  selectAllowedCourses,
  selectTenantInfo,
  selectUser,
  selectUserCourses,
} from '../store/selectors';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { bundlePurchased, updateCourseSortOrder, } from '../store/course.actions';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { arrayDiffById } from '../common/collection-utils';
import { Title } from '@angular/platform-browser';
import { setSchoolNameAsPageTitle } from '../common/seo-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { subscriberInfoMessageShown } from '../store/user.actions';
import { createDefaultDialogConfig } from '../common/dialog-utils';
import { ResponsiveLayoutService } from '../services/responsive-layout.service';
import { Breakpoints } from '@angular/cdk/layout';
import { MessagesService } from '../shared/services/messages.service';
import uniq from 'uniq';
import { PurchasesService } from '../services/purchases.service';
import { TrackingService } from '../services/tracking.service';
import { LoadingService } from '../shared/services/loading.service';
import { TenantInfo } from '../models/tenant.model';

import { calculateTrialDaysLeft } from '../common/trial-utils';

import { UrlBuilderService } from '../services/url-builder.service';
import { User } from '../models/user.model';
import { TranslateModule } from '@ngx-translate/core';
import { PageFooterComponent } from '../shared/page-footer/page-footer.component';
import { CoursesListComponent } from '../courses-list/courses-list.component';
import { DenyRoleDirective } from '../shared/auth/deny-role.directive';
import { MatButtonModule } from '@angular/material/button';
import { AllowRoleDirective } from '../shared/auth/allow-role.directive';
import { AsyncPipe, NgIf, NgStyle } from '@angular/common';
import { AiGlobalChatService } from '../ai-global-chat/ai-global-chat.service';
import {isLimitedLifetimePlan} from '../common/appsumo-utils';
import { OchPopupModalComponent } from '../shared/och-popup-modal/och-popup-modal.component';
import { QuestionnaireComponent } from './questionnaire/questionnaire.component';

declare const Stripe;

interface CoursesData {
  isLoggedOut: boolean;
  isAdmin: boolean;
  isCourseAdmin: boolean;
  allowedCourses: string[];
  allCourses: Course[];
  userCourses: Course[];
  isUserSubscribed: boolean;
  tenant: TenantInfo;
  trialDaysLeft: number;
  user: User;
}

interface SuggestTitlesChatOutput {
  suggestedTitle: string;
}

@Component({
  selector: 'courses',
  templateUrl: './courses.component.html',
  styleUrls: ['./courses.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    AllowRoleDirective,
    MatButtonModule,
    NgStyle,
    DenyRoleDirective,
    CoursesListComponent,
    PageFooterComponent,
    AsyncPipe,
    TranslateModule,
    QuestionnaireComponent
  ],
})
export class CoursesComponent implements OnInit, OnDestroy {
  data$: Observable<CoursesData>;

  numberOfGridColumns = 2;
  gridGutterSize = '1px';
  phonePortrait: boolean;

  // for images
  nocoursesimgurl: string;

  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    private ub: UrlBuilderService,
    private messages: MessagesService,
    private title: Title,
    private route: ActivatedRoute,
    private responsive: ResponsiveLayoutService,
    private router: Router,
    private purchases: PurchasesService,
    private tracking: TrackingService,
    private loading: LoadingService,
    private aiChat: AiGlobalChatService
  ) {
  }

  ngOnInit() {

    // for images
    this.nocoursesimgurl = this.ub.buildImageUrl('images/course-add-dialog/no-courses.png');

    const subDomainChanged =
      this.route.snapshot.queryParamMap.get('subDomainChanged'),
      activatePlan = this.route.snapshot.queryParamMap.get('activatePlan'),
      planFrequency = this.route.snapshot.queryParamMap.get('planFrequency'),
      activateTrial = this.route.snapshot.queryParamMap.get('activateTrial'),
      bundleCode = this.route.snapshot.queryParamMap.get('bundleCode'),
      purchaseResult = this.route.snapshot.queryParamMap.get('purchaseResult'),
      platformPlanPurchasedEvent = this.route.snapshot.queryParamMap.get(
        'platformPlanPurchasedEvent'
      ),
      platformPurchaseEvent = this.route.snapshot.queryParamMap.get(
        'platformPurchaseEvent'
      ),
      platformPurchaseEventValue = this.route.snapshot.queryParamMap.get(
        'platformPurchaseEventValue'
      ),
      accountCreationEvent = !!this.route.snapshot.queryParamMap.get(
        "accountCreationEvent"
      );

    if (subDomainChanged) {
      // this.messages.success(`Your new website is now available here at ${subDomainChanged}, you need to login.`);
      this.messages.i18nSuccess(
        'courses.messages.yourNewWebsiteIsAvailableHere',
        { subDomainChanged }
      );
      window.history.replaceState(null, null, window.location.pathname);
    }

    if (purchaseResult == 'success' && bundleCode) {
      const ongoingPurchaseSessionId = this.route.snapshot.queryParamMap.get(
        'ongoingPurchaseSessionId'
      );

      window.history.replaceState(null, null, window.location.pathname);

      this.processBundlePurchaseCompletion(ongoingPurchaseSessionId);
    }

    const courses$ = this.store.pipe(select(selectAllCourses));

    const user$: Observable<any> = this.store.pipe(select(selectUser));

    const isLoggedOut$ = this.store.pipe(
      select(isLoggedOut),
      filter((isLoggedOut) => isLoggedOut != undefined)
    );

    const isUserSubscribed$ = this.store.pipe(select(isUserSubscribed));

    const isAdmin$ = this.store.pipe(select(isAdmin));

    const userCourses$ = this.store.pipe(
      select(selectUserCourses),
      map((userCourses) => (userCourses ? uniq(userCourses) : []))
    );

    const isSubscriberInfoMessageShown$ = this.store.pipe(
      select(isSubscriberInfoMessageShown)
    );

    const tenant$ = this.store.pipe(select(selectTenantInfo));

    const isCourseAdmin$ = this.store.pipe(select(isCourseAdmin));

    const allowedCourses$ = this.store.pipe(select(selectAllowedCourses));

    this.data$ = combineLatest([
      user$,
      isLoggedOut$,
      isAdmin$,
      courses$,
      userCourses$,
      isUserSubscribed$,
      isSubscriberInfoMessageShown$,
      tenant$,
      isCourseAdmin$,
      allowedCourses$,
      user$,
    ]).pipe(
      filter(
        ([
          user,
          isLoggedOut,
          isAdmin,
          courses,
          userCourses,
          isUserSubscribed,
          isSubscriberInfoMessageShown,
          tenant,
        ]) =>
          (isLoggedOut && !!courses) ||
          (!isLoggedOut && !!user && !!courses && !!tenant)
      ),
      map(
        ([
          user,
          isLoggedOut,
          isAdmin,
          allCourses,
          userCourses,
          isUserSubscribed,
          isSubscriberInfoMessageShown,
          tenant,
          isCourseAdmin,
          allowedCourses,
        ]) => {
          return {
            user,
            isLoggedOut,
            isAdmin,
            allCourses,
            userCourses,
            isUserSubscribed,
            isSubscriberInfoMessageShown,
            tenant,
            trialDaysLeft: calculateTrialDaysLeft(tenant),
            isCourseAdmin,
            allowedCourses,
          };
        }
      ),
      tap((data) => {
        if (data.isUserSubscribed && !data.isSubscriberInfoMessageShown) {
          // this.messages.info('As a subscriber, you have access to all courses!');
          this.messages.i18nInfo(
            'courses.messages.asASubscriberYouHaveAccessToAll'
          );
          this.store.dispatch(subscriberInfoMessageShown());
        }
      }),
      debounceTime(10)
    );

    setSchoolNameAsPageTitle(this.store, this.title);

    this.responsive
      .matchesAny(Breakpoints.Handset, Breakpoints.TabletPortrait)
      .subscribe((isMobileMode) => {
        this.numberOfGridColumns = isMobileMode ? 1 : 2;
        this.gridGutterSize = isMobileMode ? '40px' : '1px';
      });

    this.responsive
      .matchesAny(Breakpoints.HandsetPortrait)
      .subscribe((phonePortrait) => (this.phonePortrait = phonePortrait));

    if (activatePlan) {
      this.router.navigate(['/admin', 'school-settings'], {
        queryParams: { activatePlan, planFrequency, activateTrial },
      });
    }

    if (platformPlanPurchasedEvent) {
      this.tracking.reportPlatformSubscription();
    }

    if (platformPurchaseEvent) {
      this.tracking.reportPlatformPurchase(platformPurchaseEventValue);
    }

    if (accountCreationEvent) {
      this.tracking.reportPlatformAccountCreation();
    }

  }

  ngOnDestroy() {
    this.messages.clear();
  }

  addNewCourse(allCourses: Course[], tenant: TenantInfo) {

    // removed AppSumo student limits
    //if (isAppSumoLimitedPlan(tenant) && allCourses?.length == tenant.appsumoTierLimits?.maxCourses) {
    //  this.messages.warn(`Maximum number of courses reached. Check the AppSumo section of the Admin screen for instructions.`);
    //  return;
    //}

    const dialogConfig = createDefaultDialogConfig();

    dialogConfig.minWidth = '560px';
    dialogConfig.maxWidth = '560px';

    this.dialog.open(AddCourseDialogComponent, dialogConfig);
  }

  onCourseMovedDown(courses: Course[], movedCourse: Course) {
    const newSortOrder = [...courses];

    const courseIndex = newSortOrder.findIndex(
      (course) => course.id == movedCourse.id
    );

    moveItemInArray(newSortOrder, courseIndex, courseIndex + 1);

    this.store.dispatch(updateCourseSortOrder({ newSortOrder }));
  }

  onCourseMovedUp(courses: Course[], movedCourse: Course) {
    const newSortOrder = [...courses];

    const courseIndex = newSortOrder.findIndex(
      (course) => course.id == movedCourse.id
    );

    moveItemInArray(newSortOrder, courseIndex, courseIndex - 1);

    this.store.dispatch(updateCourseSortOrder({ newSortOrder }));
  }

  availableCourses(allCourses: Course[], userCourses: Course[]) {
    return arrayDiffById(allCourses || [], userCourses || []).filter(
      (course) => course.status == 'published' && !course.hiddenCourseOn
    );
  }

  showAllCourses(data: CoursesData) {
    return (
      data.isLoggedOut ||
      data.isAdmin ||
      data.isCourseAdmin ||
      data.isUserSubscribed ||
      data.allCourses.filter((course) => course.status == 'published').length ==
      0
    );
  }

  showMyCourses(data: CoursesData) {
    return (
      !data.isLoggedOut &&
      !data.isAdmin &&
      !data.isCourseAdmin &&
      !data.isUserSubscribed
    );
  }

  allCoursesList(data: CoursesData): Course[] {
    // if the user is a student
    if (!data.isAdmin && !data.isCourseAdmin) {

      const publishedCourses = data.allCourses.filter(course => course.status == 'published');

      if (data.user?.pricingPlan == 'lifetime') {
        return publishedCourses;
      }

      return publishedCourses.filter(course => !course.hiddenCourseOn);

    } else {
      return data.allCourses;
    }
  }

  processBundlePurchaseCompletion(ongoingPurchaseSessionId: string) {
    this.purchases
      .waitForPurchaseCompletion(
        ongoingPurchaseSessionId,
        'courses.messages.bundleActivated'
      )
      .subscribe((purchaseSession) => {
        this.tracking.reportStudentPurchase(purchaseSession.price);

        this.store.dispatch(
          bundlePurchased({ bundleId: purchaseSession.bundleId })
        );
      });
  }

  openAiChat(allCourses: Course[]) {

    let allCourseTitles = 'The creator has not created any courses yet.';

    if (allCourses?.length) {
      allCourseTitles = allCourses.map(course => course.title).join(", ");
    }

    this.aiChat.startGlobalAiChatSession({
      promptId: 'courseIdeasSuggestions',
      promptParams: { allCourseTitles },
      welcomeMessage: 'Tell me about your course, and I will give you title suggestions.',
      chatOutputCallBack: (output: SuggestTitlesChatOutput) => {
        alert("chat output received: " + JSON.stringify(output));
      }
    });
  }

  questionaireVisible(data: CoursesData) {

    const isWebsiteOwner = data?.tenant["email"] == data?.user.email;

    const answered = data?.tenant?.questionnaireSettings?.answered;

    return !answered && isWebsiteOwner;
  }
}
